/* eslint-disable style/quote-props */
import type { SnackBarConfig } from '@shared/components/Snackbar';
import type { User } from '../types/types.auth';
import { Link } from '@mui/material';
import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '@shared/components/Buttons/Button';
import Input from '@/shared/components/Input';
import { LoadingSpinner } from '@shared/components/LoadingSpinner';
import SEO from '@/shared/components/SEO';
import CustomSnackbar from '@shared/components/Snackbar';
import { UserContext } from '@/shared/contexts/UserContext';
import { authService } from '@shared/services/AuthService';

interface LoginFormProps {
  contextMessage?: string;
  handleSuccess?: (user: User) => void;
  isModal?: boolean;
  onSwitchToSignUp?: (
    contextMessage?: string,
    handleSuccess?: (user: User) => void,
    isModal?: boolean,
    signUpFlag?: string,
  ) => void;
  signUpFlag?: string;
}

function LoginForm({
  contextMessage,
  handleSuccess,
  isModal = false,
  onSwitchToSignUp,
  signUpFlag,
}: LoginFormProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [snackbarConfig, setSnackbarConfig] = useState<SnackBarConfig>();
  const { setUser, refetchUser } = useContext(UserContext);
  const navigate = useNavigate();

  const handleSnackBarClose = () => {
    if (!snackbarConfig)
      return;
    setSnackbarConfig({ ...snackbarConfig, open: false });
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const user = await authService.authenticateUser(email, password);
      if ('newPasswordRequired' in user) {
        setSnackbarConfig({
          type: 'info',
          message: 'New password required. Please update your password.',
          open: true,
        });
      }
      else {
        setUser(user);
        try {
          await refetchUser({
            email: user.email,
            idToken: user?.idToken || '',
          });
          if (handleSuccess) {
            handleSuccess(user);
          }
          else if (!isModal) {
            const needsOnboarding = !user.networkOnboardingComplete;
            if (needsOnboarding) {
              navigate('/onboarding');
            }
            else {
              const storedPath = sessionStorage.getItem('preLoginPath');
              const redirectPath = storedPath || '/my-reviews';
              sessionStorage.removeItem('preLoginPath');
              navigate(redirectPath);
            }
          }
        }
        catch (fetchError: any) {
          // Show error when user details fetch fails
          setSnackbarConfig({
            type: 'error',
            message: fetchError?.message || 'Failed to load user data. Please try again.',
            open: true,
          });
        }
      }
    }
    catch (error: any) {
      setSnackbarConfig({
        type: 'error',
        message: error?.message || 'Login failed. Please try again.',
        open: true,
      });
    }
    finally {
      setIsLoading(false);
    }
  };

  const socialLogin = (provider: 'LinkedIn' | 'Google') => {
    const domain = import.meta.env.VITE_COGNITO_DOMAIN;
    const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
    const redirectUri = import.meta.env.VITE_COGNITO_REDIRECT_URI;
    const scope = encodeURIComponent('openid email profile');

    // Store current page context for post-login redirect
    sessionStorage.setItem('preLoginPath', window.location.pathname + window.location.search);

    const authUrl = `${domain}/oauth2/authorize?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&identity_provider=${provider}&scope=${scope}`;
    window.location.href = authUrl;
  };

  const handleSwitchToSignUp = () => {
    if (onSwitchToSignUp) {
      onSwitchToSignUp(contextMessage, handleSuccess, isModal, signUpFlag);
    }
    else if (!isModal) {
      navigate('/sign-up');
    }
  };

  return (
    <>
      <SEO
        title="Kandir - Sign In"
        description="Sign in to Kandir, your AI co-pilot for B2B deal navigation. Access real, peer-sourced deal process documentation, generate AI-driven strategies, and close deals faster."
        ogImage="https://app.kandir.io/kandir_logo.png"
        twitterImage="https://app.kandir.io/kandir_logo.png"
      />
      <div className={`auth-form ${isModal ? 'auth-form--modal' : ''}`}>
        {isLoading && (
          <div className="loader-overlay">
            <LoadingSpinner variant="circular" size="large" message="Loading, please wait..." fullScreen />
          </div>
        )}

        <div className="form-header">
          <h1 className="form-title">Welcome back to Kandir</h1>
          <p className="form-subtitle">
            {contextMessage || 'Sign in to continue'}
          </p>
        </div>

        <form className="form" onSubmit={handleLogin}>
          <div className="field">
            <label htmlFor="email" className="label">Email</label>
            <Input
              type="email"
              testId="sign-in-email"
              name="email"
              currentValue={email}
              onChange={e => setEmail(e.target.value)}
              fullWidth
            />
          </div>

          <div className="field">
            <label htmlFor="password" className="label">Password</label>
            <Input
              type="password"
              name="password"
              testId="sign-in-password"
              currentValue={password}
              onChange={e => setPassword(e.target.value)}
              fullWidth
            />
          </div>

          <div className="link-wrapper link-wrapper--forgot-password">
            <Link href="/forgot-password" underline="hover" className="link link--small">
              Forgot your password?
            </Link>
          </div>

          <div className="mt-4">
            <Button
              type="submit"
              fullWidth
              disabled={isLoading}
              data-testid="auth-submit-button"
            >
              Sign in
            </Button>
          </div>
        </form>

        <div className="divider divider--spacing-md">or</div>

        <div className="auth-form__social">
          <button
            type="button"
            onClick={() => socialLogin('Google')}
            className="button-social"
            disabled={isLoading}
            data-testid="google-login-button"
          >
            <img
              src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/google/google-original.svg"
              alt="Google"
            />
            Sign in with Google
          </button>
          <button
            type="button"
            onClick={() => socialLogin('LinkedIn')}
            className="button-social"
            disabled={isLoading}
            data-testid="linkedin-login-button"
          >
            <img
              src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/linkedin/linkedin-original.svg"
              alt="LinkedIn"
            />
            Sign in with LinkedIn
          </button>
        </div>

        <div className="link-wrapper link-wrapper--switch">
          Don't have an account?
          <button
            type="button"
            onClick={handleSwitchToSignUp}
            className="link link--button link--regular"
          >
            Sign up
          </button>
        </div>

        <CustomSnackbar config={snackbarConfig} setOpen={handleSnackBarClose} />
      </div>
    </>
  );
}

export default LoginForm;
